import { A } from '@ember/array';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Mixin from '@ember/object/mixin';
import { enqueueTask, dropTask } from 'ember-concurrency';
import { CLASSIFICATION_TYPES } from 'sh-www/models/classification';
import LightboxOverlay from 'sh-www/components/modals/overlays/lightbox';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { showModal } from 'sh-www/utils/modal-controls';
import { getClassificationIdsFromObject, mapClassificationsToCategories } from 'sh-www/helpers/admin/map-classifications-to-categories';
import { ADMIN_CONTENT_SEARCH } from 'sh-www/constants/routes/admin/content-search';

const {
  FILTERS,
  FILTERS_PROPERTY_NAMES,
  FILTERS_OBJECT,
  PROJECT_DIGEST_INCLUDES,
  SORTS,
  SORT_NAMES,
  ORDERS,
} = ADMIN_CONTENT_SEARCH;

const FilterProps = Mixin.create(FILTERS_OBJECT);

const mixins = [FilterProps];

class AdminContentController extends Controller.extend(...mixins) {
  get queryParams() {
    return ['page', ...FILTERS_PROPERTY_NAMES];
  }

  /** Register services */
  @service api;
  @service moment;
  @service shData;
  @service store;
  @service('error-tracker') errorTracker;
  @service('state-managers/application') applicationStateManager;
  @service('video-thumbnail-from-url') videoThumbnailService;
  @service('commands/classification-commands') classificationCommandService;
  @service('commands/projectdigest-commands') projectDigestCommandService;

  /** @type {Number} */
  page = 1;

  /** @type {Pagination|null} */
  pagination = null;

  /** @type {String} one of `FILTERS.SORT.*` */
  sortValue = this[FILTERS.SORT] || SORTS.UPDATED_AT;;

  /** @type {String} one of `FILTERS.ORDER.*` */
  orderValue = this[FILTERS.ORDER] || ORDERS[0];

  /** @type {Array<Country>} */
  availableCountries = [];

  /** @type {Array<Service>} */
  availableServices = [];

  /** @type {Array<VideoCategory>} */
  availableVideoCategories = [];

  /** @type {Boolean} */
  @computed('loadServices.isRunning','loadVideoCategories.isRunning','loadClassifications.isRunning')
  get isLoading() {
    return this.loadServices.isRunning
      || this.loadVideoCategories.isRunning
      || this.loadClassifications.isRunning;
  }

  /** @type {Boolean} */
  @computed('loadProjectDigests.isRunning')
  get isSearching() {
    return this.loadProjectDigests.isRunning;
  }

  /** @type {Array<Classification>} */
  @computed('loadClassifications.lastSuccessful.value')
  get classificationItems() {
    return mapClassificationsToCategories(this.loadClassifications.lastSuccessful?.value);
  }

  /** @type {Object} */
  @computed(...FILTERS_PROPERTY_NAMES)
  get filters() {
    return FILTERS_PROPERTY_NAMES.reduce((object, filter) => {
      object[filter] = this[filter];
      return object;
    }, {});
  }

  /** @type {Boolean} */
  @computed(`filters.{${FILTERS_PROPERTY_NAMES.join(',')}}`)
  get isClean() {
    const { filters } = this;
    return FILTERS_PROPERTY_NAMES.every((filter) => filters[filter] === null);
  }

  /** @type {Array<Object>} */
  @computed('sortValue')
  get sortOptions() {
    const { sortValue } = this;

    return Object.entries(SORT_NAMES).map(([value, title]) => ({
      title,
      value,
      selected: value === sortValue,
    }));
  }

  @dropTask
  *loadCountries() {
    const { store } = this;
    let countries = store.peekAll('country');

    if (!countries.length) {
      countries = yield store.findAll('country');
    }

    this.set('availableCountries', countries.sortBy('name'));
  }

  @dropTask
  *loadServices() {
    const { store } = this;
    let services = store.peekAll('service');

    if (!services.length) {
      services = yield store.findAll('service', {
        adapterOptions: { isPublic: true },
      });
    }

    this.set('availableServices', services);
  }

  @dropTask
  *loadVideoCategories() {
    const { store } = this;
    let videoCategories = store.peekAll('video-category');

    if (!videoCategories.length) {
      videoCategories = yield store.findAll('video-category', {
        adapterOptions: { isPublic: true },
      });
    }

    this.set('availableVideoCategories', videoCategories);
  }

  @dropTask
  *loadClassifications() {
    try {
      return yield this.classificationCommandService.loadClassifications({
        filters: {
          types: [
            CLASSIFICATION_TYPES.TYPE_DEFAULT,
            CLASSIFICATION_TYPES.TYPE_ADMIN_ONLY,
          ],
        },
      });
    } catch (error) {
      this.errorTracker.error('loadClassifications - failed to fetch classifications', this, error);
      this.applicationStateManager.addToast("Failed to load classifications. Please try again.");
    }
  }

  @enqueueTask
  *updateProjectdigestClassifications(classificationIds, projectdigestId) {
    try {
      return yield this.projectDigestCommandService.updateProjectdigestClassifications(projectdigestId, classificationIds)
    } catch (error) {
      this.errorTracker.error(`updateProjectdigestClassifications - failed to update classifications for projectdigest: ${projectdigestId}`, this, error);
      this.applicationStateManager.addToast("Failed to update classifications for project. Please try again.");
    }
  }

  @enqueueTask
  *updateProjectdigestFeatured(featured, projectdigestId) {
    try {
      return yield this.projectDigestCommandService.updateProjectdigestFeaturedStatus(projectdigestId, featured);
    } catch (error) {
      this.errorTracker.error(`updateProjectdigestFeatured - failed to update featured status for projectdigest: ${projectdigestId}`, this, error);
      this.applicationStateManager.addToast("Failed to update featured status for project. Please try again.");
    }
  }

  @dropTask
  *loadProjectDigests() {
    const { page } = this;
    const options = { params: { page } };

    FILTERS_PROPERTY_NAMES.forEach((filter) => {
      const value = this[filter];

      if (value !== null) {
        if (filter === FILTERS.SORT || filter === FILTERS.ORDER) {
          options.params[filter] = value;
        } else {
          options.params.filter = options.params.filter || {};
          options.params.filter[filter] = value;
        }
      }
    });

    this.shData.addIncludesToOptions(options, PROJECT_DIGEST_INCLUDES);

    try {
      const response = yield this.api.request('/projectdigests', options);
      this.shData.pushResponseToStore(response);

      this.setProperties({
        page,
        pagination: this.shData.getPaginationModelFromResponse(response),
        model: { projectDigests: this.shData.getModelArrayFromResponse(response, 'projectdigest') },
      });
    } catch (error) {
      handleErrorResponseWithModal(this, error);
    }
  }

  @action
  setSortValue(value) {
    this.setProperties({
      sortValue: value,
      [FILTERS.SORT]: value,
      [FILTERS.ORDER]: null,
    });
  }

  @action
  setOrderValue(value) {
    this.setProperties({
      orderValue: value,
      [FILTERS.ORDER]: value,
    });
  }

  /**
   * @param {Ember.Object} data
   */
  @action
  handleSetFilters(data) {
    this.setProperties(FILTERS_PROPERTY_NAMES.reduce((object, filter) => {
      object[filter] = data[filter];
      return object;
    }, { page: 1 }));
  }

  @action
  handlePageChange(increment) {
    this.set('page', this.page + increment);
  }

  @action
  handleAddToast(message) {
    this.applicationStateManager.addToast(message);
  }

  @action
  handleGetThumbnail(link) {
    return this.videoThumbnailService.getThumb(link);
  }

  @action
  handleOpenVideoPlayer(projectDigest) {
    showModal(this, LightboxOverlay, {
      digestsToShowForVideoCarousel: A([projectDigest]),
    });
  }

  @action
  setFeaturedChanged(newValue, projectdigestId) {
    this.updateProjectdigestFeatured.perform(newValue, projectdigestId);
  }

  @action
  setClassificationsChanged(newValues, projectdigestId) {
    const classificationIds = getClassificationIdsFromObject(newValues);
    this.updateProjectdigestClassifications.perform(classificationIds, projectdigestId);
  }
}

export default AdminContentController;
